import { Button, Checkbox, Tooltip } from 'antd'
import { gcpStorageUrl } from '../../../../consts'
import $t from '../../../../i18n'
import { STEPS } from 'consts/flowConsts'
import { planExtraInfo } from 'consts/planConsts'
import { useDispatch } from 'react-redux'
import Actions from '../../../../actions'
import routes from 'consts/routes'
import { getPlanFileUrl } from 'utils/planUtils'
import useApplicationValidation from 'hooks/useApplicationValidation'

import './Plan.css'

const Plan = ({
  plan,
  isDetailedView,
  displayRecommendedFlag,
  isCompareDisabled,
  isFavoriteDisabled,
  isCompareChecked,
  isFavorite
}) => {
  const dispatch = useDispatch()
  useApplicationValidation()
  // the title is what before the ()
  const nameSplit = plan?.translated_name?.split('(')
  const title = nameSplit?.[0]
  // the description is what is inside the ()
  const description = nameSplit?.[1]?.replaceAll('(', '').replaceAll(')', '')
  const planFileUrl = getPlanFileUrl(plan)

  const planDetails = plan?.translated_details
  const onEnrollNow = () => {
    // update selected plan
    dispatch(Actions.createAction(Actions.PLAN_SELECTED, { plan }))
    dispatch(
      Actions.createAction(Actions.STEP_COMPLETED, { step: STEPS.PLANS })
    )
  }

  const planFavoriteChanged = () => {
    isFavorite
      ? dispatch(Actions.createAction(Actions.PLAN_SAVE_REMOVED, { plan }))
      : dispatch(Actions.createAction(Actions.PLAN_SAVE_ADDED, { plan }))
  }

  const planCompareChanged = e => {
    if (e?.target?.checked) {
      dispatch(Actions.createAction(Actions.PLAN_COMPARE_ADDED, { plan }))
      // also when adding to compare, we should add to saved tab
      dispatch(Actions.createAction(Actions.PLAN_SAVE_ADDED, { plan }))
    } else {
      dispatch(Actions.createAction(Actions.PLAN_COMPARE_REMOVED, { plan }))
    }
  }

  const onSeeDetails = () => {
    dispatch(Actions.createAction(Actions.PLAN_SELECTED, { plan }))
    // navigate to plan details
    dispatch(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        routes.ENROLL_PLAN?.replace(':planId', plan?.antidote_plan_id)
      )
    )
  }

  return (
    <div className='plan-info'>
      <div className='plan-header'>
        <img
          className='plan-img'
          alt=''
          src={`${gcpStorageUrl}/${planDetails?.color?.toLowerCase()}.svg`}
        />
        <div className='plan-title-container'>
          <div className='plan-title semi-bold'>{title}</div>
          <div className='plan-description'>{description}</div>
        </div>
        {plan?.isRecommended && displayRecommendedFlag && (
          <div className='plan-recommended'>
            {$t('enroll.plans.tabs.recommended')}
          </div>
        )}
        <i
          className={`plan-favorite-icon ${
            isFavorite ? 'icon-like-full' : 'icon-like-empty'
          }`}
          data-disabled={isFavoriteDisabled}
          onClick={planFavoriteChanged}
        />
      </div>
      <div className='plan-body'>
        <div className='plan-field'>
          <div className='plan-field-title semi-bold'>
            {$t('enroll.plans.plan.monthlyPremium')}:
          </div>
          <h2 className='plan-field-value semi-bold'>
            ${plan?.plan_rate_multiplied}
          </h2>
        </div>
        <div className='plan-field'>
          <div className='plan-field-title semi-bold'>
            {$t('enroll.plans.plan.annualDeductible')}:
          </div>
          <h2 className='plan-field-value semi-bold'>
            {planDetails?.Annual_Deductible}
          </h2>
        </div>
        <div className='plan-fields'>
          <div className='plan-field row'>
            <div className='plan-field-title semi-bold'>
              {$t('enroll.plans.plan.maxOop')}:
            </div>
            <div className='plan-field-value semi-bold'>
              {planDetails?.Max_Out_of_Pocket}
            </div>
          </div>
          <div className='plan-field row'>
            <div className='plan-field-title semi-bold'>
              {$t('enroll.plans.plan.pcpVisit')}:
            </div>
            <div className='plan-field-value semi-bold'>
              {planDetails?.PCP_Visit}
            </div>
            {!isDetailedView && (
              <Tooltip
                placement='bottom'
                disabled={!isDetailedView}
                title={planDetails?.Primary_Care_Visit_Details?.replaceAll(
                  '/',
                  '\n'
                )}
              >
                <i className='icon-info' />
              </Tooltip>
            )}
          </div>
          <div className='plan-field row'>
            <div className='plan-field-title semi-bold'>
              {$t('enroll.plans.plan.specialistVisit')}:
            </div>
            <div className='plan-field-value semi-bold'>
              {planDetails?.Specialist_Visit}
            </div>
            {!isDetailedView && (
              <Tooltip
                placement='bottom'
                disabled={!isDetailedView}
                title={planDetails?.Specialist_Visit_Details?.replaceAll(
                  '/',
                  '\n'
                )}
              >
                <i className='icon-info' />
              </Tooltip>
            )}
          </div>
          <div className='plan-field row'>
            <div className='plan-field-title semi-bold'>
              {$t('enroll.plans.plan.genericDrugs')}:
            </div>
            <div className='plan-field-value semi-bold'>
              {planDetails?.Generic_Drugs}
            </div>
            {!isDetailedView && (
              <Tooltip
                placement='bottom'
                disabled={!isDetailedView}
                title={planDetails?.Generic_Drugs_Details?.replaceAll(
                  '/',
                  '\n'
                )}
              >
                <i className='icon-info' />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {isDetailedView && (
        <div className='plan-extended-info'>
          {planExtraInfo.map(infoRow => (
            <div className='plan-extended-info-row row' key={infoRow?.key}>
              <div className='icon-container'>
                <i className={`icon-${infoRow?.icon}`} />
              </div>
              <div className='plan-extended-info-field'>
                <div className='plan-extended-info-title'>
                  {$t(`enroll.plans.plan.extraInfo.${infoRow?.key}`)}:
                </div>
                <div className='plan-extended-info-value'>
                  {planDetails?.[infoRow?.value]}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div
        className={
          isDetailedView
            ? 'plan-footer not-share-friendly column'
            : 'plan-footer not-share-friendly row'
        }
      >
        {!isDetailedView && (
          <Checkbox
            className='compare-checkbox'
            onChange={planCompareChanged}
            disabled={isCompareDisabled}
            checked={isCompareChecked}
          >
            {$t('enroll.plans.plan.compare')}
          </Checkbox>
        )}
        <div className='plan-footer-buttons'>
          {!isDetailedView && (
            <Button
              className='plan-details-btn trackable'
              data-name='See details'
              onClick={onSeeDetails}
            >
              {$t('common.seeDetails')}
            </Button>
          )}
          {isDetailedView && planFileUrl && (
            <Button
              className='plan-download-btn trackable'
              data-name='Download Description'
              onClick={() => {
                planFileUrl && window.open(getPlanFileUrl(plan), '_blank')
              }}
            >
              {$t('enroll.plans.plan.downloadDesc')}
            </Button>
          )}
          <Button
            className='plan-enroll-btn trackable'
            data-name='Enroll now'
            type='primary'
            onClick={onEnrollNow}
          >
            {$t('common.enrollNow')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Plan
